/* eslint-disable react/display-name */
import { CloseOutlined, EditOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Input, Space, Tag, Typography } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useDispatch } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { useAccountsByCompanyId, useAccountsValues } from 'src/features/companyAccounts/companyAccounts.hooks';
import styles from 'src/components/pages/Company/CompanyPage/CompanyPage.module.css';
import { fetchAccounts } from 'src/features/companyAccounts/companyAccounts.state';
import {
  addAccountToCompany,
  fetchCompanyAccounts,
  removeAccountFromCompany,
} from 'src/features/companies/companies.state';
import ConfirmButton from 'src/components/atoms/ConfirmButton';
import { useDebounce } from 'react-use';

const CompanyAccountsTab: React.FC = () => {
  const { companyId } = useParams() as { companyId: string };
  const dispatch = useDispatch();

  const [createMode, setCreateMode] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');

  const accounts = useAccountsValues();
  const companyAccounts = useAccountsByCompanyId({ companyId });

  const isAccountInCompany = useCallback(
    (id: string) => companyAccounts.map(c => c.id).includes(id),
    [companyAccounts]
  );

  const accountActions = useCallback(
    (id: string) => {
      const isInCompany = isAccountInCompany(id);
      if (isInCompany) {
        return (
          <ConfirmButton
            danger
            size='small'
            onClick={() => dispatch(removeAccountFromCompany({ accountId: id, companyId }))}
          >
            {isInCompany ? 'Удалить' : 'Добавить'}
          </ConfirmButton>
        );
      }
      return (
        <Button size='small' onClick={() => dispatch(addAccountToCompany({ companyId, body: { ids: [id] } }))}>
          {isInCompany ? 'Удалить' : 'Добавить'}
        </Button>
      );
    },
    [companyId, dispatch, isAccountInCompany]
  );

  const currentAccounts = useMemo(() => {
    return createMode ? accounts : companyAccounts;
  }, [accounts, companyAccounts, createMode, searchValue]);

  useEffect(() => {
    dispatch(fetchAccounts({}));
    dispatch(fetchCompanyAccounts({ companyId }));
  }, [companyId, dispatch]);

  useDebounce(
    () => {
      if (searchValue !== undefined) {
        dispatch(fetchAccounts({ email: searchValue, name: searchValue }));
      }
    },
    300,
    [searchValue, dispatch]
  );

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Typography.Title level={4}>Аккаунты</Typography.Title>
        <Button onClick={() => setCreateMode(m => !m)}>{createMode ? <CloseOutlined /> : <EditOutlined />}</Button>
      </div>
      <Input
        onChange={e => setSearchValue(e.target.value)}
        allowClear
        autoFocus
        placeholder='Введите почту или имя пользвателя'
      />
      <div className={styles.content}>
        {currentAccounts.map(account => (
          <div key={account.id} className={styles.listTile}>
            <Link to={`/commercial/accounts/${account.id}`} className={styles.tileName}>
              <Space>
                <UserOutlined />
                <span>
                  {account.fullname} ({account.email})
                </span>
              </Space>
            </Link>
            <Space>
              {account.role && <Tag>{account.role}</Tag>}
              {account.disabled && <Tag color='red'>Архивирован</Tag>}
              {createMode && accountActions(account.id)}
            </Space>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CompanyAccountsTab;
